import { useState } from 'react'
import { isHTTPError } from 'ky'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import { AlertCircle, ShieldOff } from 'lucide-react'
import { api } from '@frontend/lib/api'
import type { ClientListItem, RevokeKeyResponse } from '@common/types'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@frontend/components/ui/dialog'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'

interface RevokeKeyModalProps {
  client: ClientListItem | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

async function revokeClientKey(clientId: string): Promise<RevokeKeyResponse> {
  return api.delete(`clients/${clientId}/key`).json<RevokeKeyResponse>()
}

export function RevokeKeyModal({ client, open, onOpenChange }: RevokeKeyModalProps) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const revokeMutation = useMutation({
    mutationFn: () => revokeClientKey(client!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clients-detailed'] })
      onOpenChange(false)
      toast.success(`API key revoked for "${client!.name}"`)
    },
    onError: (err) => {
      if (isHTTPError(err)) {
        const data = err.data as { error?: string }
        setError(data.error || 'Failed to revoke API key')
      } else {
        setError('Failed to revoke API key')
      }
    },
  })

  const handleRevoke = () => {
    setError(null)
    revokeMutation.mutate()
  }

  const isSubmitting = revokeMutation.isPending

  if (!client) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <ShieldOff className="h-5 w-5" />
            Revoke API Key
          </DialogTitle>
          <DialogDescription>
            Are you sure you want to revoke the API key for <strong>{client.name}</strong>?
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="flex items-start gap-3 p-3 bg-amber-50 text-amber-800 rounded-md text-sm">
            <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">This action cannot be undone</p>
              <p className="mt-1">
                Revoking the API key will immediately disable Drata sync for this client. You will need to add a new key
                to re-enable synchronization.
              </p>
            </div>
          </div>
        </div>

        {/* Error display */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-md text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleRevoke} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Revoking...
              </>
            ) : (
              'Revoke Key'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
